import moment from 'moment'
import type { Moment } from 'moment'

import { formatTimespan } from 'app/common/dateHelper'

const REDUCED_REST_ORDINAL = 'ReducedRestOrdinal'

type ReducedRestOrdinal = {
  readonly __brand: typeof REDUCED_REST_ORDINAL
  readonly count: number
}

const parseReducedRestOrdinal = (
  count: number,
): ReducedRestOrdinal | undefined => {
  if (!Number.isInteger(count) || count < 1) return undefined
  return { __brand: REDUCED_REST_ORDINAL, count }
}

export type WorkPeriodSource = {
  startTime: Moment
  endTime: Moment
  duration: string
  drivingTime: string
  dailyDrivingTimeExtensionNo?: number
}

export type WorkPeriodInk = {
  readonly start: string | null
  readonly end: readonly [string, string, string] | null
}

export type DailyRestSource = {
  restDuration: string
  formalRestDuration: string
  isReducedRest: boolean
  dailyReducedRestNumber: number
}

export type ActivityProvenance = {
  isManualEntry: boolean
  isModifiedByUser: boolean
}

export type SegmentMark =
  | { readonly kind: 'neither' }
  | { readonly kind: 'manual' }
  | { readonly kind: 'edited' }
  | { readonly kind: 'both' }

export const PRINT_SEGMENT_CLASS: {
  readonly manual: 'manual-entry-mark'
  readonly edited: 'activity-print'
} = {
  manual: 'manual-entry-mark',
  edited: 'activity-print',
}

export const PAPER_PROVENANCE_LEGEND: readonly {
  className: 'manual-entry-mark' | 'activity-print'
  label: string
}[] = [
  { className: PRINT_SEGMENT_CLASS.manual, label: 'Wpis manualny' },
  { className: PRINT_SEGMENT_CLASS.edited, label: 'Zmieniony' },
]

const windowEnd = (chartStart: Moment, chartDays: number): Moment =>
  moment(chartStart).add(chartDays, 'days')

const chartOwns = (
  chartStart: Moment,
  chartDays: number,
  instant: Moment,
): boolean => {
  const at = moment(instant)
  return at.isSameOrAfter(chartStart) && at.isBefore(windowEnd(chartStart, chartDays))
}

const clockText = (instant: Moment): string => moment(instant).format('HH:mm')

const drivingText = (
  drivingTime: string,
  extensionNo: number | undefined,
): string => {
  const text = formatTimespan(drivingTime)
  if (
    extensionNo !== undefined &&
    Number.isInteger(extensionNo) &&
    extensionNo >= 1
  ) {
    return `${text} (${extensionNo}x10)`
  }
  return text
}

const endCluster = (
  sigma: string,
  driving: string,
  endClock: string,
): readonly [string, string, string] => [sigma, driving, endClock]

export const workPeriodInk = (input: {
  chartStart: Moment
  chartDays: number
  period: WorkPeriodSource
}): WorkPeriodInk => {
  const { chartStart, chartDays, period } = input
  const start = chartOwns(chartStart, chartDays, period.startTime)
    ? clockText(period.startTime)
    : null
  const end = chartOwns(chartStart, chartDays, period.endTime)
    ? endCluster(
        formatTimespan(period.duration),
        drivingText(period.drivingTime, period.dailyDrivingTimeExtensionNo),
        clockText(period.endTime),
      )
    : null
  return { start, end }
}

const restPair = (taken: string, formal: string): string =>
  `${taken} / ${formal}`

const restPairWithOrdinal = (
  taken: string,
  formal: string,
  ordinal: ReducedRestOrdinal,
): string => `${taken} (${ordinal.count}x9) / ${formal}`

export const dailyRestCaption = (rest: DailyRestSource): string => {
  const taken = formatTimespan(rest.restDuration)
  const formal = formatTimespan(rest.formalRestDuration)
  if (!rest.isReducedRest) return restPair(taken, formal)
  const ordinal = parseReducedRestOrdinal(rest.dailyReducedRestNumber)
  if (!ordinal) return restPair(taken, formal)
  return restPairWithOrdinal(taken, formal, ordinal)
}

export const segmentMark = (activity: ActivityProvenance): SegmentMark => {
  if (activity.isManualEntry && activity.isModifiedByUser) return { kind: 'both' }
  if (activity.isManualEntry) return { kind: 'manual' }
  if (activity.isModifiedByUser) return { kind: 'edited' }
  return { kind: 'neither' }
}
